import React from 'react';
import { motion } from 'framer-motion';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useFirestore } from '../hooks/useFirestore';

echarts.use([
  BarChart,
  PieChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer
]);

const Analytics = () => {
  const { documents: agencies, loading: agenciesLoading } = useFirestore('agencies');
  const { documents: articles, loading: articlesLoading } = useFirestore('articles');
  
  const loading = agenciesLoading || articlesLoading;

  // Revenue by Agency
  const revenueData = agencies.map(agency => ({
    name: agency.name,
    value: agency.total_price
  }));

  // Article Status Distribution
  const statusData = [
    {
      name: 'Completed',
      value: articles.filter(a => a.status === 'completed').length
    },
    {
      name: 'In Progress',
      value: articles.filter(a => a.status === 'in_progress').length
    },
    {
      name: 'Draft',
      value: articles.filter(a => a.status === 'draft').length
    }
  ];

  // Article Usage by Niche Type
  const nicheData = agencies.reduce((acc, agency) => {
    const type = agency.niche_type;
    if (!acc[type]) {
      acc[type] = { total: 0, used: 0, remaining: 0 };
    }
    acc[type].total += agency.total_articles;
    acc[type].used += (agency.total_articles - agency.remaining_articles);
    acc[type].remaining += agency.remaining_articles;
    return acc;
  }, {});

  const revenueChartOptions = {
    title: {
      text: 'Revenue by Agency',
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} LEI ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        name: 'Revenue',
        type: 'pie',
        radius: '50%',
        data: revenueData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };

  const statusChartOptions = {
    title: {
      text: 'Article Status Distribution',
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        name: 'Status',
        type: 'pie',
        radius: '50%',
        data: statusData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };

  const nicheChartOptions = {
    title: {
      text: 'Article Usage by Niche Type',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    legend: {
      data: ['Total', 'Used', 'Remaining']
    },
    xAxis: {
      type: 'category',
      data: Object.keys(nicheData)
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        name: 'Total',
        type: 'bar',
        data: Object.values(nicheData).map(d => d.total),
        itemStyle: {
          color: '#3b82f6'
        }
      },
      {
        name: 'Used',
        type: 'bar',
        data: Object.values(nicheData).map(d => d.used),
        itemStyle: {
          color: '#22c55e'
        }
      },
      {
        name: 'Remaining',
        type: 'bar',
        data: Object.values(nicheData).map(d => d.remaining),
        itemStyle: {
          color: '#f59e0b'
        }
      }
    ]
  };

  const totalRevenue = agencies.reduce((sum, agency) => sum + agency.total_price, 0);
  const averageOrderValue = totalRevenue / agencies.length || 0;
  const totalArticles = agencies.reduce((sum, agency) => sum + agency.total_articles, 0);
  const averageArticlePrice = totalRevenue / totalArticles || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
        <p className="text-gray-600">Insights into your article management business</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-primary-600">{totalRevenue.toLocaleString()} LEI</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Average Order Value</h3>
          <p className="text-3xl font-bold text-success-600">{averageOrderValue.toFixed(2)} LEI</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Articles</h3>
          <p className="text-3xl font-bold text-indigo-600">{articles.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Avg. Article Price</h3>
          <p className="text-3xl font-bold text-purple-600">{averageArticlePrice.toFixed(2)} LEI</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <ReactEChartsCore
            echarts={echarts}
            option={revenueChartOptions}
            style={{ height: '400px' }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <ReactEChartsCore
            echarts={echarts}
            option={statusChartOptions}
            style={{ height: '400px' }}
          />
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <ReactEChartsCore
            echarts={echarts}
            option={nicheChartOptions}
            style={{ height: '400px' }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agency Performance</h3>
          <div className="space-y-4">
            {agencies.map((agency) => {
              const usagePercentage = ((agency.total_articles - agency.remaining_articles) / agency.total_articles * 100).toFixed(1);
              
              return (
                <div key={agency.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{agency.name}</div>
                    <div className="text-xs text-gray-500">{agency.total_price.toLocaleString()} LEI</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${usagePercentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600">{usagePercentage}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;